import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Input,
  Menu,
  MenuItem,
  MenuProps,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useStyles } from '../CaseDiagnosisStyles'
import TuneIcon from '@mui/icons-material/Tune'
import Draggable from 'react-draggable'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CloseIcon from '@mui/icons-material/Close'

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    style={{ left: '1188px !important' }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    width: '300px',
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '&:active': {
        // backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
      '&:hover': {
        backgroundColor: '#fff',
      },
    },
  },
}))

export default function ImageFilter({ imgFilterValue, setImgFilterValue, stain }) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const classes = useStyles()
  //const [value, setValue] = React.useState<number | string | Array<number | string>>(100)
  const [isDragging, setIsDragging] = useState(false)
  const [sync, setSync] = useState(imgFilterValue && imgFilterValue.sync)

  useEffect(() => {
    if (imgFilterValue.sync) {
      setSync(true)
    } else {
      setSync(false)
    }
  }, [imgFilterValue])

  useEffect(() => {
    if (sync) {
      const greenValue = imgFilterValue.gammaG
      imgFilterValue.gammaR = greenValue
      imgFilterValue.gammaB = greenValue
      imgFilterValue.sync = sync
      setImgFilterValue(imgFilterValue)
    }
  }, [sync])

  const handleFilterClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleFilterClose = async () => {
    setAnchorEl(null)
  }
  const valuetext = (value: number) => {
    return `${value}`
  }

  const handleSliderChange = (filterName: string) => async (event: Event, newValue: number | number[]) => {
    if (sync && filterName === 'gammaG') {
      setImgFilterValue((prevData) => ({
        ...prevData,
        gammaR: newValue,
        gammaG: newValue,
        gammaB: newValue,
      }))
    } else {
      setImgFilterValue((prevData) => ({
        ...prevData,
        [filterName]: newValue,
      }))
    }
  }

  const handleDragStart = () => {
    setIsDragging(true)
  }

  const handleDragStop = () => {
    setIsDragging(false)
  }
  const dragHandleStyle = {
    cursor: isDragging ? 'grabbing' : 'grab',
  }

  const handleInputChange = (filterName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = Number(event.target.value)
    // Ensure the value is within the desired range
    if (inputValue < 0) {
      inputValue = 0
    } else if (inputValue > 200) {
      inputValue = 200
    }
    setImgFilterValue((prevData) => ({
      ...prevData,
      [filterName]: Number(inputValue),
    }))
  }

  const handleResetFilter = () => {
    setImgFilterValue({
      brightness: 100,
      contrast: 100,
      sharpness: 0,
      gammaR: 100,
      gammaG: 100,
      gammaB: 100,
      sync: true,
    })
  }

  const handleSyncChange = (event) => {
    setSync(event.target.checked)
    setImgFilterValue((prevData) => ({
      ...prevData,
      sync: event.target.checked,
    }))
  }

  const handleSliderDrag = (e: any) => {
    e.stopPropagation()
  }

  const sliderToTextboxValue = (sliderValue) => {
    return ((sliderValue + 3) * 100) / 3
  }

  const textboxToSliderValue = (textboxValue) => {
    return (textboxValue * 3) / 100 - 3
  }

  const handleTextboxChange = (event) => {
    const newValue = parseFloat(event.target.value)
    if (!isNaN(newValue)) {
      setImgFilterValue((prevData) => ({
        ...prevData,
        ['sharpness']: textboxToSliderValue(newValue),
      }))
    }
  }

  return (
    <>
      <Tooltip title="Filter" placement="top-start">
        <Button variant="outlined" disableElevation onClick={handleFilterClick} className={classes.filterButton}>
          <TuneIcon style={{ color: '#fff' }} />
        </Button>
      </Tooltip>
      <Draggable onStart={handleDragStart} onStop={handleDragStop}>
        <StyledMenu
          id="demo-customized-menu"
          MenuListProps={{
            'aria-labelledby': 'demo-customized-button',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleFilterClose}
          style={dragHandleStyle}
        >
          <Box className={classes.slideDetails}>
            <div className={classes.dropdownTitle}>
              <div className={classes.dropdownLabel}>
                Filters
                <Tooltip title="Close">
                  <CloseIcon style={{ float: 'right', cursor: 'pointer' }} onClick={handleFilterClose} />
                </Tooltip>
                <Tooltip title="Reset">
                  <RestartAltIcon style={{ float: 'right', cursor: 'pointer' }} onClick={handleResetFilter} />
                </Tooltip>
              </div>
            </div>
            <div onMouseDown={handleSliderDrag}>
              <MenuItem disableRipple>
                <div>
                  <div>
                    <Box sx={{ width: 300 }}>Brightness</Box>
                  </div>
                  <div>
                    <Box sx={{ width: 300 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                          <Slider
                            className={classes.sliderColor}
                            defaultValue={20}
                            getAriaValueText={valuetext}
                            step={1}
                            valueLabelDisplay="auto"
                            min={0}
                            max={200}
                            value={typeof imgFilterValue.brightness === 'number' ? imgFilterValue.brightness : 0}
                            onChange={handleSliderChange('brightness')}
                            onDragStart={handleSliderDrag}
                          />
                        </Grid>
                        <Grid item>
                          <Input
                            value={typeof imgFilterValue.brightness === 'number' ? imgFilterValue.brightness : 0}
                            size="small"
                            onChange={handleInputChange('brightness')}
                            //onBlur={handleBlur}
                            inputProps={{
                              min: 0,
                              max: 200,
                              type: 'number',
                            }}
                            sx={{ width: '43px', margin: '0px 24px 0px 0px', padding: '0px 0px 0px 0px' }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </div>
                </div>
              </MenuItem>
              <MenuItem disableRipple>
                <div>
                  <div>
                    <Box sx={{ width: 300 }}>Contrast</Box>
                  </div>
                  <div>
                    <Box sx={{ width: 300 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                          <Slider
                            className={classes.sliderColor}
                            defaultValue={20}
                            getAriaValueText={valuetext}
                            step={1}
                            valueLabelDisplay="auto"
                            min={0}
                            max={200}
                            value={typeof imgFilterValue.contrast === 'number' ? imgFilterValue.contrast : 0}
                            onChange={handleSliderChange('contrast')}
                          />
                        </Grid>
                        <Grid item>
                          <Input
                            value={typeof imgFilterValue.brightness === 'number' ? imgFilterValue.contrast : 0}
                            size="small"
                            onChange={handleInputChange('contrast')}
                            //onBlur={handleBlur}
                            inputProps={{
                              min: 0,
                              max: 200,
                              type: 'number',
                            }}
                            sx={{ width: '43px', margin: '0px 24px 0px 0px', padding: '0px 0px 0px 0px' }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </div>
                </div>
              </MenuItem>
              <MenuItem disableRipple>
                <div>
                  <div>
                    <Box sx={{ width: 300 }}>Sharpness</Box>
                  </div>
                  <div>
                    <Box sx={{ width: 300 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                          <Slider
                            className={classes.sliderColor}
                            aria-label="Custom marks"
                            getAriaValueText={valuetext}
                            step={0.1}
                            min={-3}
                            max={3}
                            value={typeof imgFilterValue.sharpness === 'number' ? imgFilterValue.sharpness : 0}
                            onChange={handleSliderChange('sharpness')}
                          />
                        </Grid>
                        <Grid item>
                          <Input
                            value={sliderToTextboxValue(imgFilterValue.sharpness).toFixed(2)}
                            size="small"
                            onChange={handleTextboxChange}
                            //onBlur={handleBlur}
                            inputProps={{
                              min: -3,
                              max: 3,
                              type: 'number',
                            }}
                            sx={{ width: '43px', margin: '0px 24px 0px 0px', padding: '0px 0px 0px 0px' }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </div>
                </div>
              </MenuItem>
              <MenuItem disableRipple>
                <div>
                  <div>
                    <Box sx={{ width: 300 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                          <p>Gamma Correction</p>
                        </Grid>
                        <Grid item>
                          <FormGroup>
                            <FormControlLabel
                              sx={{ marginRight: '26px' }}
                              control={
                                <Checkbox
                                  checked={
                                    imgFilterValue && imgFilterValue.sync !== undefined ? imgFilterValue.sync : sync
                                  }
                                  onChange={handleSyncChange}
                                />
                              }
                              label="Sync"
                            />
                          </FormGroup>
                        </Grid>
                      </Grid>
                    </Box>
                  </div>
                  <div>
                    <Box sx={{ width: 300 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                          <Slider
                            className={classes.sliderColor}
                            aria-label="Custom marks"
                            defaultValue={20}
                            getAriaValueText={valuetext}
                            step={1}
                            valueLabelDisplay="auto"
                            min={0}
                            max={200}
                            value={
                              typeof imgFilterValue.gammaR === 'number'
                                ? sync
                                  ? imgFilterValue.gammaG
                                  : imgFilterValue.gammaR
                                : 0
                            }
                            onChange={handleSliderChange('gammaR')}
                            disabled={sync}
                          />
                        </Grid>
                        <Grid item>
                          R:{' '}
                          <Input
                            value={
                              typeof imgFilterValue.gammaR === 'number'
                                ? sync
                                  ? imgFilterValue.gammaG
                                  : imgFilterValue.gammaR
                                : 0
                            }
                            size="small"
                            onChange={handleInputChange('gammaR')}
                            //onBlur={handleBlur}
                            inputProps={{
                              min: 0,
                              max: 200,
                              type: 'number',
                            }}
                            sx={{ width: '43px', margin: '0px 24px 0px 0px', padding: '0px 0px 0px 0px' }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box sx={{ width: 300 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                          <Slider
                            className={classes.sliderColor}
                            aria-label="Custom marks"
                            defaultValue={20}
                            getAriaValueText={valuetext}
                            step={10}
                            valueLabelDisplay="auto"
                            min={0}
                            max={200}
                            value={typeof imgFilterValue.gammaG === 'number' ? imgFilterValue.gammaG : 0}
                            onChange={handleSliderChange('gammaG')}
                          />
                        </Grid>
                        <Grid item>
                          G:{' '}
                          <Input
                            value={typeof imgFilterValue.gammaG === 'number' ? imgFilterValue.gammaG : 0}
                            size="small"
                            onChange={handleInputChange('gammaG')}
                            //onBlur={handleBlur}
                            inputProps={{
                              min: 0,
                              max: 200,
                              type: 'number',
                            }}
                            sx={{ width: '43px', margin: '0px 24px 0px 0px', padding: '0px 0px 0px 0px' }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box sx={{ width: 300 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                          <Slider
                            className={classes.sliderColor}
                            aria-label="Custom marks"
                            defaultValue={20}
                            getAriaValueText={valuetext}
                            step={1}
                            valueLabelDisplay="auto"
                            min={0}
                            max={200}
                            value={
                              typeof imgFilterValue.gammaB === 'number'
                                ? sync
                                  ? imgFilterValue.gammaG
                                  : imgFilterValue.gammaB
                                : 0
                            }
                            onChange={handleSliderChange('gammaB')}
                            disabled={sync}
                          />
                        </Grid>
                        <Grid item>
                          B:{' '}
                          <Input
                            value={
                              typeof imgFilterValue.gammaB === 'number'
                                ? sync
                                  ? imgFilterValue.gammaG
                                  : imgFilterValue.gammaB
                                : 0
                            }
                            size="small"
                            onChange={handleInputChange('gammaB')}
                            //onBlur={handleBlur}
                            inputProps={{
                              min: 0,
                              max: 200,
                              type: 'number',
                            }}
                            sx={{ width: '43px', margin: '0px 24px 0px 0px', padding: '0px 0px 0px 0px' }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </div>
                </div>
              </MenuItem>
            </div>
          </Box>
        </StyledMenu>
      </Draggable>
    </>
  )
}
